/* Histórico de versões de um documento legal (ADR-0774). Cada linha é uma VERSÃO (não uma linha de
   idioma): os idiomas aparecem como badges, porque status e vigência são propriedades da versão.

   A regra de imutabilidade é ESTRUTURAL aqui: "Editar", "Publicar" e "Remover" só existem no ramo
   `draft`. Versão publicada não recebe um botão desabilitado (que sugere "talvez dê") — recebe
   "Ver" e o atalho "Nova versão a partir desta", que é literalmente a saída que o 409 prescreve.

   `Vigora em` (effectiveAt) e `Publicado em` (publishedAt) são colunas SEPARADAS de propósito: são
   fatos diferentes (jurídico × administrativo) e é a diferença entre eles que torna visível o
   "publicar hoje, vigorar em 30 dias". Colapsá-las numa só apagaria o recurso. */
import {
  Button,
  Checkbox,
  EmptyState,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@planuze/ui';
import { ScaleIcon } from 'lucide-react';
import { useLegalAdminContent } from '../../content/sections/legal-admin.js';
import { fmtDateTime } from '../../lib/format.js';
import {
  type LegalLifecycle,
  type LegalVersionGroup,
  primaryLegalRow,
} from '../../lib/legal-versions.js';

/** Cor por situação (não é texto — não varia por locale). `current` é a que o público lê agora;
    `scheduled` ainda não vigora. */
const LIFECYCLE_VARIANTS: Record<LegalLifecycle, 'neutral' | 'success' | 'warning'> = {
  draft: 'neutral',
  scheduled: 'warning',
  current: 'success',
  superseded: 'neutral',
};

interface Props {
  readonly groups: readonly LegalVersionGroup[];
  /** super_admin — MANAGE é deliberadamente mais restrito que o FAQ (publicar termos é ato
      jurídico, não edição de conteúdo). Sem isso, só as ações de leitura aparecem. */
  readonly canManage: boolean;
  readonly onView: (group: LegalVersionGroup) => void;
  readonly onEdit: (group: LegalVersionGroup) => void;
  readonly onPublish: (group: LegalVersionGroup) => void;
  readonly onDelete: (group: LegalVersionGroup) => void;
  /** Cria a versão seguinte copiando esta — a saída prescrita para corrigir texto publicado. */
  readonly onCopy: (group: LegalVersionGroup) => void;
  /** Alguma mutação em flight — desabilita as ações destrutivas/irreversíveis. */
  readonly busy: boolean;
  readonly onCreate: () => void;
  readonly selectedIds?: ReadonlySet<string>;
  readonly onToggle?: (id: string) => void;
  readonly onToggleAll?: (checked: boolean) => void;
}

export function LegalVersionsTable({
  groups,
  canManage,
  onView,
  onEdit,
  onPublish,
  onDelete,
  onCopy,
  busy,
  onCreate,
  selectedIds,
  onToggle,
  onToggleAll,
}: Props) {
  const copy = useLegalAdminContent().versionsTable;
  if (groups.length === 0) {
    return (
      <EmptyState
        icon={<ScaleIcon className="size-6" />}
        title={copy.emptyTitle}
        description={copy.emptyDescription}
        primaryAction={
          canManage ? <Button onClick={onCreate}>{copy.createButton}</Button> : undefined
        }
      />
    );
  }
  const draftIds = groups.flatMap((group) => {
    const id = group.status === 'draft' ? group.locales[0]?.id : undefined;
    return id === undefined ? [] : [id];
  });
  const selection = selectedIds ?? new Set<string>();
  const bulkEnabled = canManage && selectedIds !== undefined;
  const allSelected = draftIds.length > 0 && draftIds.every((id) => selection.has(id));
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {bulkEnabled && (
            <TableHead className="w-10">
              <Checkbox
                checked={allSelected}
                disabled={draftIds.length === 0}
                onCheckedChange={(checked) => onToggleAll?.(checked === true)}
                aria-label={copy.selectPageAriaLabel}
              />
            </TableHead>
          )}
          <TableHead>{copy.columnVersion}</TableHead>
          <TableHead>{copy.columnTitle}</TableHead>
          <TableHead>{copy.columnSituation}</TableHead>
          <TableHead>{copy.columnLanguages}</TableHead>
          <TableHead>{copy.columnEffectiveAt}</TableHead>
          <TableHead>{copy.columnPublishedAt}</TableHead>
          <TableHead>{copy.columnActions}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {groups.map((group) => {
          const primary = primaryLegalRow(group);
          const isDraft = group.status === 'draft';
          const representativeId = group.locales[0]?.id;
          return (
            <TableRow key={group.version}>
              {bulkEnabled && (
                <TableCell>
                  {isDraft && representativeId !== undefined && (
                    <Checkbox
                      checked={selection.has(representativeId)}
                      onCheckedChange={() => onToggle?.(representativeId)}
                      aria-label={copy.selectRowAriaLabel(group.version)}
                    />
                  )}
                </TableCell>
              )}
              <TableCell numeric className="font-medium">
                v{group.version}
              </TableCell>
              <TableCell>{primary?.title ?? '—'}</TableCell>
              <TableCell>
                <StatusBadge variant={LIFECYCLE_VARIANTS[group.lifecycle]} dot>
                  {copy.lifecycleLabels[group.lifecycle]}
                </StatusBadge>
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {group.locales.map((row) => (
                    <StatusBadge
                      key={row.id}
                      variant={row.status === 'published' ? 'success' : 'neutral'}
                      title={row.status === 'published' ? copy.published : copy.draft}
                    >
                      {row.locale}
                    </StatusBadge>
                  ))}
                </div>
              </TableCell>
              <TableCell className="text-muted-foreground">
                {group.effectiveAt === null ? '—' : fmtDateTime(group.effectiveAt)}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {group.publishedAt === null ? '—' : fmtDateTime(group.publishedAt)}
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-2">
                  {isDraft && canManage ? (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => onEdit(group)}>
                        {copy.edit}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() => onPublish(group)}
                      >
                        {copy.publish}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy}
                        onClick={() => onDelete(group)}
                      >
                        {copy.remove}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => onView(group)}>
                        {copy.view}
                      </Button>
                      {canManage && (
                        <Button variant="ghost" size="sm" onClick={() => onCopy(group)}>
                          {copy.copyFromThis}
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
